import React, { useEffect, useMemo, useState } from "react";
import { Link } from "expo-router";
import MetaHead from "@/components/MetaHead";
import PublicFooter from "@/components/PublicFooter";
import categories from "@/constants/useCaseHubCategories";
import films from "@/constants/useCaseFilms.json";
import { useCasesApi } from "@/lib/api/useCases";
import "@/components/Pages/ChurchesPage.css";
import "@/components/Pages/UseCaseEditorial.css";

export default function UseCasesIndexPage() {
  const [active, setActive] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    useCasesApi
      .list()
      .then((data) => {
        if (alive) setItems(Array.isArray(data?.useCases) ? data.useCases : []);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [retry]);
  const groups = useMemo(() => {
    const grouped = new Map();
    for (const item of items) {
      const category = item.hubCategory || "business";
      if (active !== "all" && category !== active) continue;
      if (!grouped.has(category)) grouped.set(category, []);
      grouped.get(category).push(item);
    }
    const order = [
      ...categories.slice(1).map((c) => c.value),
      ...grouped.keys(),
    ];
    return [...new Set(order)]
      .filter((key) => grouped.has(key))
      .map((key) => ({
        key,
        label: categories.find((c) => c.value === key)?.label || key,
        items: grouped.get(key),
      }));
  }, [items, active]);
  const graph = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Gab AI Use Cases: AI Tools for Every Profession",
    url: "https://gab.ai/use-cases",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        url: `https://gab.ai/use-cases/${item.slug}`,
      })),
    },
  };
  return (
    <div className="church-page uc-hub">
      <MetaHead
        title="Gab AI Use Cases: AI Tools for Every Profession"
        description="Explore AI use cases for writers, marketers, developers, teachers, homeschoolers, sales teams, designers, churches, and more. Find tools and examples for your work."
        canonical="/use-cases"
        keywords="AI use cases, AI tools, AI for business, AI for creators, AI for developers, AI for homeschoolers, Gab AI"
        structuredData={graph}
      />
      <main
        id="main-content"
        className="church-scroll"
        data-landing-scroll="true"
      >
        <div className="church-content">
          <header className="church-hero uc-hub-hero">
            <p className="church-eyebrow">
              One platform. So many possibilities.
            </p>
            <h1>
              Built for how
              <br />
              <span>you work.</span>
            </h1>
            <p className="church-intro">
              From your next idea to your everyday work. Find AI tools,
              practical prompts, and a little more room to do what matters.
            </p>
            <div className="church-actions">
              <Link className="church-cta" href="/signup">
                Try Gab AI free <span aria-hidden="true">↗</span>
              </Link>
              <Link
                className="church-cta church-cta-secondary"
                href="/pricing#business"
              >
                Explore team plans <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </header>
          <nav
            className="uc-hub-filters"
            aria-label="Filter use cases by category"
          >
            {categories.map((c) => (
              <button
                key={c.value}
                type="button"
                aria-pressed={active === c.value}
                onClick={() => setActive(c.value)}
              >
                {c.label}
              </button>
            ))}
          </nav>
          <div
            className="uc-hub-results"
            aria-live="polite"
            aria-busy={loading}
          >
            {loading && <p role="status">Loading use cases…</p>}
            {!loading && error && (
              <div>
                <p>We couldn’t load the use cases. Please try again.</p>
                <button
                  type="button"
                  className="church-cta church-cta-secondary"
                  onClick={() => setRetry((n) => n + 1)}
                >
                  Try again
                </button>
              </div>
            )}
            {!loading &&
              !error &&
              groups.map((group) => (
                <section
                  key={group.key}
                  aria-labelledby={`category-${group.key}`}
                  className="uc-hub-group"
                >
                  <h2 id={`category-${group.key}`}>{group.label}</h2>
                  <div className="uc-hub-grid">
                    {group.items.map((item) => (
                      <Link
                        key={item.slug}
                        className="uc-hub-card"
                        href={`/use-cases/${item.slug}`}
                      >
                        {films[item.slug] && (
                          <img
                            src={films[item.slug].poster}
                            alt=""
                            loading="lazy"
                            width="1920"
                            height="1080"
                          />
                        )}
                        <div>
                          <h3>
                            {item.name}
                            <span aria-hidden="true">↗</span>
                          </h3>
                          <p>{item.subtitle}</p>
                          <span className="uc-hub-card-action">
                            Explore the possibilities
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            {!loading && !error && !groups.length && (
              <p>No use cases in this category yet.</p>
            )}
          </div>
          <section className="church-section church-closing">
            <p className="church-eyebrow">Your ideas belong here</p>
            <h2>
              Make something
              <br />
              <span>that matters.</span>
            </h2>
            <p>
              Find the right tools for your ideas, from writing and research to
              images and presentations. Bring your whole team together with centrally
              managed billing, account administration, and a shared credit pool.
            </p>
            <div className="church-actions">
              <Link className="church-cta" href="/signup">
                Try Gab AI free <span aria-hidden="true">↗</span>
              </Link>
              <Link
                className="church-cta church-cta-secondary"
                href="/pricing#business"
              >
                Gab AI for Business <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </section>
        </div>
        <PublicFooter variant="editorial" showHero={false} />
      </main>
    </div>
  );
}
